<script setup>
import CaretLeftIcon from '@iconify-vue/ph/caret-left';
import CaretRightIcon from '@iconify-vue/ph/caret-right';
import { computed, ref, watch } from 'vue';

const props = defineProps({
    table: {
        type: Object,
        required: true,
    },
    maxPages: {
        type: Number,
        required: false,
        default: 5,
    },
});

const currentPage = ref(props.table.getState().pagination.pageIndex + 1);

const currentPageSize = ref(props.table.getState().pagination.pageSize);

const totalRows = ref(props.table.getCoreRowModel().rows.length);

const totalPages = computed(() => props.table.getPageCount());

function goToPage() {
    props.table.setPageIndex(currentPage.value - 1);
}

function setCurrentPage(page) {
    currentPage.value = page;
    goToPage(page);
}

const pagesWrapped = computed(() => {
    let pages = [];
    const wrapping = Math.floor(props.maxPages / 2);

    for (
        let i = Math.max(currentPage.value - wrapping, 1);
        i <= Math.min(totalPages.value, currentPage.value + wrapping);
        i++
    ) {
        pages.push(i);
    }

    const pagesLength = pages.length;

    if (pagesLength < totalPages.value) {
        if (pages[0] === 1) {
            for (
                let i = pages[pages.length - 1] + 1;
                i <= Math.min(totalPages.value, props.maxPages);
                i++
            ) {
                pages.push(i);
            }
        } else {
            for (
                let i = pages[0] - 1;
                i >= Math.max(1, totalPages.value - props.maxPages + 1);
                i--
            ) {
                pages.unshift(i);
            }
        }
    }

    return pages;
});

watch(props.table.getState(), () => {
    currentPage.value = props.table.getState().pagination.pageIndex + 1;
    currentPageSize.value = props.table.getState().pagination.pageSize;
    totalRows.value = props.table.getCoreRowModel().rows.length;
});

watch(currentPageSize, () => {
    props.table.setPageSize(currentPageSize.value);
});
</script>

<template>
    <ul v-if="totalPages > 1" class="inline-flex items-center">
        <li>
            <button
                @click="
                    currentPage === 1
                        ? setCurrentPage(1)
                        : setCurrentPage(currentPage - 1)
                "
                class="ml-0 block h-[28px] rounded-l border border-gray-200 bg-white px-3 py-1 text-sm duration-150 hover:border-indigo-600 hover:bg-indigo-600 hover:text-white"
            >
                <CaretLeftIcon class="h-4 w-4" />
            </button>
        </li>

        <li class="lg:hidden">
            <input
                min="1"
                :max="totalPages"
                :value="currentPage"
                @change="(event) => setCurrentPage(event.target.value)"
                type="number"
                class="h-[28px] w-20 border px-3 py-1 text-center"
            />
        </li>

        <li v-if="pagesWrapped[0] !== 1" class="hidden lg:block">
            <button
                @click="setCurrentPage(1)"
                class="h-[28px] border border-gray-200 bg-white px-3 py-1 text-sm duration-150 hover:border-indigo-600 hover:bg-indigo-600 hover:text-white"
            >
                1
            </button>
        </li>

        <li v-if="pagesWrapped[0] !== 1" class="hidden lg:block">
            <button
                class="h-[28px] cursor-auto border border-gray-200 bg-white px-3 py-1 text-sm"
            >
                ...
            </button>
        </li>

        <li
            class="hidden lg:block"
            v-for="i in totalPages"
            v-if="totalPages <= maxPages"
        >
            <button
                @click="setCurrentPage(i)"
                class="h-[28px] border border-gray-200 bg-white px-3 py-1 text-sm duration-150 hover:border-indigo-600 hover:bg-indigo-600 hover:text-white"
                v-if="i !== currentPage"
            >
                {{ i }}
            </button>
            <button
                @click="setCurrentPage(i)"
                aria-current="page"
                class="z-10 h-[28px] border border-gray-200 bg-gray-200 px-3 py-1 text-sm text-indigo-600"
                v-else
            >
                {{ i }}
            </button>
        </li>

        <li class="hidden lg:block" v-for="i in pagesWrapped" v-else>
            <button
                @click="setCurrentPage(i)"
                class="h-[28px] border border-gray-200 bg-white px-3 py-1 text-sm duration-150 hover:border-indigo-600 hover:bg-indigo-600 hover:text-white"
                v-if="i !== currentPage"
            >
                {{ i }}
            </button>

            <button
                @click="setCurrentPage(i)"
                aria-current="page"
                class="z-10 h-[28px] border border-gray-200 bg-gray-200 px-3 py-1 text-sm text-indigo-600"
                v-else
            >
                {{ i }}
            </button>
        </li>

        <li
            v-if="pagesWrapped[pagesWrapped.length - 1] !== totalPages"
            class="hidden lg:block"
        >
            <button
                class="h-[28px] cursor-auto border border-gray-200 bg-white px-3 py-1 text-sm"
            >
                ...
            </button>
        </li>

        <li
            v-if="pagesWrapped[pagesWrapped.length - 1] !== totalPages"
            class="hidden lg:block"
        >
            <button
                @click="setCurrentPage(totalPages)"
                class="h-[28px] border border-gray-200 bg-white px-3 py-1 text-sm duration-150 hover:border-indigo-600 hover:bg-indigo-600 hover:text-white"
            >
                {{ totalPages }}
            </button>
        </li>

        <li>
            <button
                @click="
                    currentPage < totalPages
                        ? setCurrentPage(currentPage + 1)
                        : null
                "
                class="ml-0 block h-[28px] rounded-r border border-gray-200 bg-white px-3 py-1 text-sm duration-150 hover:border-indigo-600 hover:bg-indigo-600 hover:text-white"
            >
                <CaretRightIcon class="h-4 w-4" />
            </button>
        </li>
    </ul>
</template>
